import { prisma } from "database";
import { PrismaAdapter } from "@auth/prisma-adapter";
import linkUserToWhiteLabel from "./linkUserToWhiteLabel";
import {
  findUniqueWhiteLabelByCustomDomain,
  findUniqueWhiteLabelBySubdomain,
} from "database/services";
import { getSubdomain } from "@sajilni/utils/getSubdomain";
import type { Awaitable } from "next-auth";
import type { Adapter, AdapterUser } from "next-auth/adapters";
import type { AuthRequestLike } from "./request-like";
import type { Prisma } from "database";

const customPrismaAdapter = (
  p: typeof prisma,
  request?: AuthRequestLike
): Adapter => {
  // Helper function to get WhiteLabel ID from request
  const getAccessibleWhiteLabelId = async (): Promise<string | undefined> => {
    if (!request) return undefined;

    const host = request.headers.get("host");
    if (!host) return undefined;

    const domain = host.replace(
      ".localhost:3000",
      `.${process.env["NEXT_PUBLIC_ROOT_DOMAIN"]}`
    );
    const subdomain = getSubdomain(domain);

    const currentWhiteLabel = subdomain
      ? await findUniqueWhiteLabelBySubdomain(subdomain)
      : await findUniqueWhiteLabelByCustomDomain(domain);

    return currentWhiteLabel?.id;
  };

  // @ts-ignore
  return {
    ...PrismaAdapter(p),
    getUser: async (id) => {
      const user = await p.user.findUnique({
        where: {
          id,
        },
        include: {
          roles: {
            include: {
              role: true,
            },
          },
        },
      });
      if (!user) return null;
      const roles = (user.roles || [])
        .map((ur) => ur.role?.name)
        .filter((r): r is string => Boolean(r));
      const adapterUser: AdapterUser & { roles?: string[] } = {
        id: user.id,
        email: user.email,
        emailVerified: user.emailVerified,
        name: user.name,
        image: user.image,
        ...(roles.length ? { roles } : {}),
      } as any;
      return adapterUser as Awaitable<AdapterUser>;
    },
    getUserByEmail: async (email: string) => {
      const whiteLabelId = await getAccessibleWhiteLabelId();
      let user;
      if (whiteLabelId) {
        user = await p.user.findUnique({
          where: {
            email_accessibleWhiteLabelId: {
              email: email.toLowerCase(),
              accessibleWhiteLabelId: whiteLabelId,
            },
          },
          include: {
            userRoleAssignments: {
              include: {
                role: true,
              },
            },
          },
        });
      }
      if (!user) {
        user = await p.user.findFirst({
          where: {
            email,
            userRoleAssignments: {
              some: {
                role: { name: "SUPER_ADMIN" },
              },
            },
          },
          include: {
            userRoleAssignments: {
              include: {
                role: true,
              },
            },
          },
        });
      }
      if (!user) return null;
      const roles = (user.userRoleAssignments || [])
        .map((ur) => ur.role?.name)
        .filter((r): r is string => Boolean(r));
      const adapterUser: AdapterUser & { roles?: string[] } = {
        id: user.id,
        email: user.email,
        emailVerified: user.emailVerified,
        name: user.name,
        image: user.image,
        ...(roles.length ? { roles } : {}),
      } as any;
      return adapterUser as Awaitable<AdapterUser>;
    },
    createUser: async (data: Prisma.UserCreateInput) => {
      // Ensure email is normalized and remove non-column/nested fields like roles
      const { roles: _roles, ...rest } = data as any;
      const whiteLabelId = await getAccessibleWhiteLabelId();
      const createData = {
        ...rest,
        email: data.email.toLowerCase(),
        ...(whiteLabelId ? { accessibleWhiteLabelId: whiteLabelId } : {}),
      } as any;
      const createdUser = await p.user.create({
        data: createData,
      });

      if (request && !whiteLabelId) {
        await linkUserToWhiteLabel(request, createdUser);
      }

      const adapterUser: AdapterUser = {
        id: createdUser.id,
        email: createdUser.email,
        emailVerified: createdUser.emailVerified,
        name: createdUser.name,
        image: createdUser.image,
      } as any;
      return adapterUser as Awaitable<AdapterUser>;
    },
  };
};

export default customPrismaAdapter;
